import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ChartTooltip from '@rio-cloud/rio-uikit/ChartTooltip';
import ComposedChart from '@rio-cloud/rio-uikit/ComposedChart';
import Legend from '@rio-cloud/rio-uikit/Legend';
import Line from '@rio-cloud/rio-uikit/Line';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';

import { AnalyticsUtils } from '@/configuration/setup/googleAnalytics';

import { PackHistoryResponse } from '@/generated/GeneratedApiTypes';
import {
    THRESHOLD_WARNING,
    THRESHOLD_DANGER,
    dateOnXAxis,
    thresholdAreas,
    thresholdReferenceLines,
} from '@/features/batteryHistoryGraph/BatteryHistoryGraphThresholds';
import { PackHistoryGraphTooltip } from '@/features/batteryHistoryGraph/PackHistoryGraphTooltip';

const WARM_COLORS: { token: string; hex: string }[] = [
    { token: 'color-warmup-crimson', hex: '#31144f' },
    { token: 'color-warmup-victoria', hex: '#493d6d' },
    { token: 'color-warmup-cadillac', hex: '#7e3e72' },
    { token: 'color-warmup-raspberry', hex: '#b23672' },
    { token: 'color-warmup-cerise', hex: '#e72472' },
    { token: 'color-warmup-charm', hex: '#c9778d' },
    { token: 'color-warmup-salmon', hex: '#f19588' },
    { token: 'color-warmup-cherokee', hex: '#f5bb89' },
    { token: 'color-warmup-corn', hex: '#fde082' },
];

const ALL_PACKS_VALUE = 'ALL';

type PackHistoryGraphProps = {
    packHistory: PackHistoryResponse[];
};

const buildGraphData = (packHistory: PackHistoryResponse[], selectedPackIds: string[]) => {
    const timestampMap = new Map<string, { timestamp: string; [key: string]: string | number | undefined }>();

    packHistory
        .filter((pack) => selectedPackIds.includes(pack.pack_id!))
        .forEach((pack) => {
            pack.history?.forEach((entry) => {
                if (!entry.timestamp) return;
                const existing = timestampMap.get(entry.timestamp) ?? { timestamp: entry.timestamp };
                existing[pack.pack_id!] = entry.state_of_health_percentage;
                existing['threshold_warning'] = THRESHOLD_WARNING;
                existing['threshold_danger'] = THRESHOLD_DANGER;
                timestampMap.set(entry.timestamp, existing);
            });
        });

    return Array.from(timestampMap.values()).sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
};

const PackHistoryGraph = ({ packHistory }: PackHistoryGraphProps) => {
    const intl = useIntl();
    const allPackIds = packHistory.map((p) => p.pack_id!).filter(Boolean);
    const [selectedIds, setSelectedIds] = useState<string[]>([ALL_PACKS_VALUE]);
    const [hoveredPackId, setHoveredPackId] = useState<string | null>(null);

    const handleChange = (newSelectedIds: string[]) => {
        if (newSelectedIds.includes(ALL_PACKS_VALUE) && !selectedIds.includes(ALL_PACKS_VALUE)) {
            setSelectedIds([ALL_PACKS_VALUE]);
        } else if (newSelectedIds.includes(ALL_PACKS_VALUE)) {
            setSelectedIds(newSelectedIds.filter((id) => id !== ALL_PACKS_VALUE));
        } else {
            const packIds = newSelectedIds.filter((id) => id !== ALL_PACKS_VALUE);
            setSelectedIds(packIds.length > 0 ? packIds : [ALL_PACKS_VALUE]);
        }
    };

    const selectedPackIds = selectedIds.includes(ALL_PACKS_VALUE) ? allPackIds : selectedIds;
    const graphData = buildGraphData(packHistory, selectedPackIds);

    const multiselectRef = useRef<HTMLDivElement>(null);
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;

    useEffect(() => {
        const dropdown = multiselectRef.current?.querySelector('.dropdown');
        if (!dropdown) return;

        let wasOpen = false;

        const observer = new MutationObserver(() => {
            const isOpen = dropdown.classList.contains('open');
            if (wasOpen && !isOpen && !selectedIdsRef.current.includes(ALL_PACKS_VALUE)) {
                AnalyticsUtils.gaPush({
                    event: 'pack_filter',
                    parameters: {
                        element_name: 'PackHistoryDropdown',
                        component_name: 'Multiselect',
                        flow_name: 'view_workshop_battery_health_data',
                        trigger: 'click',
                        selected_packs: selectedIdsRef.current,
                        selected_packs_count: selectedIdsRef.current.length,
                    },
                    userProps: {},
                });
            }
            wasOpen = isOpen;
        });

        observer.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);
    const options = [
        { id: ALL_PACKS_VALUE, label: intl.formatMessage({ id: 'intl-msg:batteryService.packHistory.allPacks' }) },
        ...allPackIds.map((id, index) => ({
            id,
            label: (
                <span className="display-flex align-items-center gap-5">
                    <svg width="12" height="12">
                        <rect width="12" height="12" fill={WARM_COLORS[index % WARM_COLORS.length].hex} />
                    </svg>
                    {`${intl.formatMessage({ id: 'intl-msg:batteryService.batteryPackLevel.pack' })} ${id}`}
                </span>
            ),
        })),
    ];

    const renderLegendContent = () => {
        const rows: string[][] = [];
        for (let i = 0; i < selectedPackIds.length; i += 4) {
            rows.push(selectedPackIds.slice(i, i + 4));
        }
        return (
            <div className="display-flex flex-column gap-5">
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} className="display-flex gap-15">
                        {row.map((packId) => {
                            const index = selectedPackIds.indexOf(packId);
                            return (
                                <div key={packId} className="display-flex align-items-center gap-5">
                                    <svg width="12" height="12">
                                        <rect
                                            width="12"
                                            height="12"
                                            fill={WARM_COLORS[index % WARM_COLORS.length].hex}
                                        />
                                    </svg>
                                    <span className="text-color-black">
                                        <FormattedMessage
                                            id="intl-msg:batteryService.packHistory.legendSoH"
                                            values={{ packId }}
                                        />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <div className="margin-bottom-5">
                <span className="text-color-darker">
                    <FormattedMessage id="intl-msg:batteryService.packHistory.selectPack" />
                </span>
            </div>
            <div
                ref={multiselectRef}
                className="margin-bottom-15 pack-history-multiselect"
                style={{ width: selectedIds.includes(ALL_PACKS_VALUE) ? '20%' : '50%' }}
            >
                <Multiselect
                    options={options}
                    value={selectedIds}
                    onChange={handleChange}
                    dropdownClassName="pack-history-dropdown"
                    multiline
                />
            </div>
            <div className="height-400">
                <ComposedChart
                    tooltip={
                        <ChartTooltip
                            content={
                                <PackHistoryGraphTooltip packHistory={packHistory} hoveredPackId={hoveredPackId} />
                            }
                        />
                    }
                    legend={
                        <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="left"
                            height={50}
                            wrapperStyle={{ marginLeft: 60 }}
                            content={renderLegendContent}
                        />
                    }
                    margin={{ left: -20, top: 20, right: 5, bottom: 0 }}
                    data={graphData}
                    dataKey={(entry: { timestamp: string }) => entry.timestamp}
                    showYAxis
                    showGrid
                    dataUnit="%"
                    areas={thresholdAreas}
                    lines={selectedPackIds.flatMap((packId, index) => [
                        <Line
                            key={`${packId}-dash`}
                            dataKey={packId}
                            dot={false}
                            activeDot={{ r: 5 }}
                            strokeWidth={3}
                            strokeColor={WARM_COLORS[index % WARM_COLORS.length].token}
                            stroke={WARM_COLORS[index % WARM_COLORS.length].hex}
                            strokeDasharray="0.5 10"
                            strokeLinecap="round"
                            type="linear"
                            connectNulls
                            onMouseEnter={() => setHoveredPackId(packId)}
                            onMouseLeave={() => setHoveredPackId(null)}
                        />,
                        <Line
                            key={`${packId}-solid`}
                            dataKey={packId}
                            dot={false}
                            activeDot={{ r: 5 }}
                            strokeWidth={3}
                            strokeColor={WARM_COLORS[index % WARM_COLORS.length].token}
                            stroke={WARM_COLORS[index % WARM_COLORS.length].hex}
                            type="linear"
                            strokeLinecap="square"
                            onMouseEnter={() => setHoveredPackId(packId)}
                            onMouseLeave={() => setHoveredPackId(null)}
                        />,
                    ])}
                    referenceLines={thresholdReferenceLines}
                    xAxisOptions={{ interval: 'preserveStartEnd', tickFormatter: dateOnXAxis }}
                    yAxisOptions={{ domain: [0, 100], allowDecimals: true, tickCount: 10 }}
                />
            </div>
        </div>
    );
};

export default PackHistoryGraph;
